import Search from "@mui/icons-material/Search";
import { Box, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useToken } from "../../server/auth/use-token";
import { authHeader } from "../../utils/auth-header";
import {
  AiImageStyleConfig,
  AiImageStyleToggle,
  type AiImageStyle,
} from "../brand-kit/components/ai-image-style-toggle";
import type { BrandInfoSelected } from "../brand-kit/types";
import { selfDriving } from "../self-driving/self-driving";

const NUM_IMAGES = 4;
const MIN_IMAGE_SIZE_BYTES = 1000;
const MIN_IMAGE_DIMENSION = 2;

type ImageResult = {
  blobUrl: string | null;
  loading: boolean;
  error: boolean;
};

const validateAndCreateBlobUrl = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }

  const blob = await response.blob();

  if (!blob.type.startsWith("image/") || blob.size < MIN_IMAGE_SIZE_BYTES) {
    throw new Error("Invalid image");
  }

  const blobUrl = URL.createObjectURL(blob);

  await new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth >= MIN_IMAGE_DIMENSION && img.naturalHeight >= MIN_IMAGE_DIMENSION) {
        resolve();
      } else {
        URL.revokeObjectURL(blobUrl);
        reject(new Error("Image has invalid dimensions"));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error("Image failed to load"));
    };
    img.src = blobUrl;
  });

  return url;
};

export const AiTab: FC<{
  onImageClick?: (imageUrl: string) => void;
  selectedImage: string | null;
  onSelectionChange?: (hasSelection: boolean) => void;
  hidden?: boolean;
  branding?: BrandInfoSelected;
  mfai_ai_image_style: boolean;
}> = ({
  onImageClick,
  selectedImage,
  onSelectionChange,
  hidden = false,
  branding,
  mfai_ai_image_style,
}) => {
  const { t } = useTranslation();
  const getToken = useToken();
  const [query, setQuery] = useState("");
  const [imageResults, setImageResults] = useState<ImageResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(branding?.ai_image_style ?? "illustration");
  const abortControllerRef = useRef<AbortController | null>(null);
  const blobUrlsRef = useRef<string[]>([]);

  const handleImageClick = (url: string): void => {
    onImageClick?.(url);
    onSelectionChange?.(true);
  };

  const cleanupBlobUrls = (): void => {
    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    blobUrlsRef.current = [];
  };

  const handleSearch = useCallback(async () => {
    const trimmedQuery = query.trim();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    cleanupBlobUrls();

    if (!trimmedQuery) {
      setImageResults([]);
      setHasSearched(false);
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const initialResults: ImageResult[] = Array.from({ length: NUM_IMAGES }, () => ({
      blobUrl: null,
      loading: true,
      error: false,
    }));
    setImageResults(initialResults);
    setHasSearched(true);

    const token = await getToken();

    const requests = Array.from({ length: NUM_IMAGES }, (_, index) =>
      (async () => {
        try {
          const { data, error } = await selfDriving.GET("/photos/ai", {
            params: {
              query: { query: trimmedQuery, style: selectedStyle },
            },
            ...authHeader(token),
            signal: abortController.signal,
          });

          if (abortController.signal.aborted) return;

          if (error || !data?.url) {
            setImageResults((prev) => {
              const updated = [...prev];
              updated[index] = { blobUrl: null, loading: false, error: true };
              return updated;
            });
            return;
          }

          try {
            const blobUrl = await validateAndCreateBlobUrl(data.url);

            if (abortController.signal.aborted) {
              URL.revokeObjectURL(blobUrl);
              return;
            }

            blobUrlsRef.current.push(blobUrl);

            setImageResults((prev) => {
              const updated = [...prev];
              updated[index] = { blobUrl, loading: false, error: false };
              return updated;
            });
          } catch {
            if (abortController.signal.aborted) return;

            setImageResults((prev) => {
              const updated = [...prev];
              updated[index] = { blobUrl: null, loading: false, error: true };
              return updated;
            });
          }
        } catch (err) {
          if (err instanceof Error && err.name === "AbortError") {
            return;
          }
          console.error(`AI image generation failed for index ${index}:`, err);
          setImageResults((prev) => {
            const updated = [...prev];
            updated[index] = { blobUrl: null, loading: false, error: true };
            return updated;
          });
        }
      })(),
    );

    await Promise.allSettled(requests);
  }, [getToken, query, selectedStyle]);

  const isLoading = imageResults.some((r) => r.loading);
  const validResults = imageResults.filter((r) => r.blobUrl !== null && !r.error);
  const hasResults = validResults.length > 0;
  const allFinishedWithNoResults =
    hasSearched && imageResults.length > 0 && !isLoading && !hasResults;

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        display: hidden ? "none" : "block",
      }}
    >
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        {t("Unique images. Can take more time to generate.")}
      </Typography>

      {mfai_ai_image_style && (
        <>
          <Stack direction="row" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mr: 2 }}>
              {t("Choose an image style")}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {AiImageStyleConfig[selectedStyle as AiImageStyle]?.name}
            </Typography>
          </Stack>
          <Box sx={{ mb: 2 }}>
            <AiImageStyleToggle
              aiImageStyle={selectedStyle}
              onChange={(v) => setSelectedStyle(v)}
            />
          </Box>
        </>
      )}

      <TextField
        fullWidth
        placeholder={t("Describe the image you want")}
        size="small"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            void handleSearch();
          }
        }}
        sx={{ mb: 2, backgroundColor: "white" }}
        slotProps={{
          input: {
            endAdornment: (
              <IconButton
                data-analytics-id="ai-tab-search-button"
                onClick={() => void handleSearch()}
                size="small"
                disabled={!query.trim()}
                sx={{ color: "text.secondary" }}
              >
                <Search />
              </IconButton>
            ),
          },
        }}
      />

      {hasResults && !isLoading && (
        <Typography variant="overline" color="text.secondary">
          {t("Generated images")}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        }}
      >
        {allFinishedWithNoResults ? (
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("Something went wrong, please try again")}
            </Typography>
          </Box>
        ) : (
          imageResults.map((result, index) => {
            if (result.error || (!result.loading && !result.blobUrl)) {
              return null;
            }

            const blobUrl = result.blobUrl;

            return (
              <Box
                key={index}
                onClick={blobUrl ? () => handleImageClick(blobUrl) : undefined}
                sx={(theme) => ({
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "background.default",
                  border:
                    selectedImage === blobUrl && !result.loading
                      ? `3px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                  aspectRatio: "3 / 4",
                  position: "relative",
                  cursor: blobUrl ? "pointer" : "default",
                  pointerEvents: blobUrl ? "auto" : "none",
                  "&:hover": {
                    borderColor: blobUrl ? theme.palette.primary.light : undefined,
                  },
                })}
              >
                {result.loading ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : blobUrl ? (
                  <Box
                    component="img"
                    src={blobUrl}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : null}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};
